// @flow

/* global document */
import * as React from 'react';

import clsx from 'clsx';
import { useDispatch } from 'react-redux';

import { NOTIFICATION_ID as CART_NOTIFICATION_ID } from '@gandi/cart';
import type { Fqdn as TFqdn } from '@gandi/domain-configuration';
import { notifyDanger, closeNotification } from '@gandi/notifications';
import { sendInternalSearch } from '@gandi/tracking';
import { useTranslator } from '@gandi/translate';
import { Base, type DialogStateProps } from '@gandi/ui';

import { FqdnCell } from 'src/Domain/FqdnCell';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { addToCart, removeFromCart } from '../../modules/actions/cart';
import { fetchIDNLookup as fetchIDNLookupAction } from '../../modules/actions/idn';
import { setPhase } from '../../modules/actions/results';
import { select as selectIDNLanguages } from '../../modules/selectors/idn';
import { selectPagination } from '../../modules/selectors/pagination';
import { suggestionThunkCallback } from '../../modules/selectors/results';
import { selectSearchValue } from '../../modules/selectors/search';
import type { ExtendedSuggestion, IDNLangAlpha2Code } from '../../types';

import { setModalState } from '../PhasesDialog/module';
import { useExtendedSuggestion } from '../ProvidedArea/modules';

import AvailabilityCell from './AvailabilityCell/AvailabilityCell';
import CartButton from './CartButton';
import SelectLangRow from './SelectLangRow';

import { DEFAULT_PHASE } from './constant';

import styles from './SuggestionRow.scss';

type Props = {
  fqdn: string,
  phasesDialogState: DialogStateProps,
};

// Important Note: the React State is only used for:
// - IDN lookup
// - phase selection callback

const ITEM_CLASSNAME = 'qa-domain-row';

export function SuggestionRow({
  fqdn,
  phasesDialogState,
}: Props): React.MixedElement | null {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const searchValue = useAppSelector(state => selectSearchValue(state));
  const IDNAvailableLanguages = useAppSelector(state =>
    selectIDNLanguages(state),
  );

  const { suggestion, valid: isValid } = useExtendedSuggestion({ fqdn });
  const currentPagination = useAppSelector(selectPagination);

  const [lang, setLang] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<?Error>();
  const [isAddingToCart, setIsAddingToCart] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (suggestion) {
      if (needsPhaseSelection() && suggestion.addedToCart) {
        // $FlowFixMe[not-an-array]
        dispatch(setPhase({ fqdn, phase: DEFAULT_PHASE }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, suggestion, fqdn]);

  if (!suggestion) return null;

  const {
    addedToCart,
    availability,
    optimistic,
    corporate,
    currency,
    error: errorSuggestion,
    prices,
    premium,
    restriction,
    tld,
  } = suggestion;

  const isReady = !!(
    availability &&
    !loading &&
    (prices || errorSuggestion || availability === 'lang_required')
  );

  const isErrorWithRestriction = errorSuggestion && !!restriction;

  const displayLangSelect = !!(
    (availability === 'lang_required' || !!lang) &&
    Array.isArray(IDNAvailableLanguages) &&
    IDNAvailableLanguages.length > 0
  );

  const phases = prices?.create?.phases ?? [];

  function needsPhaseSelection() {
    return isValid && phases.length > 1;
  }

  const fetchIDNLookup = (
    currentFqdn: TFqdn,
    currentLang: IDNLangAlpha2Code,
  ): Promise<void> => {
    setLang(currentLang);
    setLoading(true);
    setError();

    const requiresLang = availability === 'lang_required';

    return dispatch(
      fetchIDNLookupAction(currentFqdn, currentLang, requiresLang),
    ).then(
      () => {
        setLoading(false);
      },
      err => {
        let message;
        switch (err._error) {
          case 'availability':
            message = __('This domain name is not available in this language.');
            break;
          case 'fetch':
          case 'prices':
          default:
            message = __(
              'Unable to fetch prices and/or availability for this domain name in this language.',
            );
            break;
        }

        dispatch(notifyDanger(message));
        setError(error);
        setLoading(false);
      },
    );
  };

  /**
   * Handle lang change.
   */
  function handleLangChange(evt: SyntheticInputEvent<>): void {
    const target = evt.target;
    const currentLang: IDNLangAlpha2Code = target.value;

    fetchIDNLookup(fqdn, currentLang);
  }

  const updateCartProducts = (lastSuggestion: ExtendedSuggestion) => {
    // Get UI position of the target in the list from the DOM
    const UIPosition =
      Array.from(document.querySelectorAll(`.${ITEM_CLASSNAME}`)).indexOf(
        document.getElementById(fqdn),
      ) + 1;
    const options = { optimistic: false, UIPosition, lang };

    sendInternalSearch({
      type: 'click',
      keyword: searchValue,
      click_rank: UIPosition,
      page: currentPagination.page,
    });

    setIsAddingToCart(true);
    if (addedToCart) {
      dispatch(removeFromCart(lastSuggestion, options))
        .catch(() =>
          // $FlowFixMe[incompatible-call] TODO
          dispatch(
            notifyDanger(__('Unable to remove the domain create in the cart.')),
          ),
        )
        .finally(() => setIsAddingToCart(false));
    } else {
      dispatch(addToCart(lastSuggestion, options))
        .catch(() => {
          // $FlowFixMe[incompatible-call] TODO
          dispatch(closeNotification(CART_NOTIFICATION_ID));
          dispatch(
            notifyDanger(__('Unable to add the domain create in the cart.')),
          );
        })
        .finally(() => setIsAddingToCart(false));
    }
  };

  const phaseSelectionCallback = suggestionThunkCallback(
    fqdn,
    updateCartProducts,
    __,
  );

  const onClickItem = async (evt: SyntheticMouseEvent<>): Promise<void> => {
    evt.preventDefault();

    if (needsPhaseSelection()) {
      if (!addedToCart) {
        dispatch(
          // $FlowFixMe[not-an-array]
          setModalState({
            phases,
            tld,
            fqdn,
            premium,
            prices,
            currency,
            callback: phaseSelectionCallback,
          }),
        );
        phasesDialogState.show();
        return;
      }
    }

    dispatch(phaseSelectionCallback);
  };

  const available = isReady && isValid;

  const langSelectTitle = addedToCart
    ? __('Remove the product from your cart to change the language')
    : __('Select a language');

  const isCartActionnable =
    !optimistic &&
    !isAddingToCart &&
    (available || addedToCart) &&
    !loading &&
    !error;

  const rootClassName = clsx(ITEM_CLASSNAME, styles.suggestion);

  return (
    <div className={rootClassName} id={fqdn}>
      <Base
        flex
        className={displayLangSelect ? styles.selectLangRow : undefined}
      >
        <FqdnCell
          cheapRenew={prices?.renew?.cheap}
          isValid={isValid}
          isErrorWithRestriction={isErrorWithRestriction}
          corporate={corporate}
          fqdn={fqdn}
          premium={premium}
        />
        {displayLangSelect && (
          <SelectLangRow
            title={langSelectTitle}
            values={IDNAvailableLanguages}
            handleChange={handleLangChange}
            value={lang}
            name={`idn-lang-${fqdn}`}
          />
        )}
      </Base>
      <div className={styles.pricesContainer}>
        <AvailabilityCell isLoading={!isReady} fqdn={fqdn} />

        {(available || availability === 'lang_required') && (
          <CartButton
            addedToCart={addedToCart}
            isCartActionnable={isCartActionnable}
            onClick={onClickItem}
            optimistic={isAddingToCart || optimistic}
          />
        )}
      </div>
    </div>
  );
}

SuggestionRow.Error = function SuggestionRowError({
  fqdn,
}: {
  fqdn: string,
}): React.MixedElement {
  return (
    <div className={styles.suggestion} id={fqdn}>
      <Base flex>
        <FqdnCell.Error fqdn={fqdn} />
      </Base>
      <div className={styles.pricesContainer}>
        <AvailabilityCell.Error />
      </div>
    </div>
  );
};
